/**
 * Processa templates de email substituindo placeholders por valores reais
 * Suporta variáveis do lead e do usuário que está enviando
 */

interface TemplateData {
  // Dados do lead
  firstName?: string;
  lastName?: string;
  fullName?: string;
  company?: string;
  email?: string;
  phone?: string;

  // Dados do usuário que está enviando
  senderName?: string;
  senderFirstName?: string;
  senderEmail?: string;

  // Variáveis PT-BR (email templates)
  nome?: string;
  primeiro_nome?: string;
  empresa?: string;
  telefone?: string;
  valor_deal?: string;
  estagio_deal?: string;
  meu_nome?: string;
  meu_email?: string;
}

/**
 * Formata um valor como moeda BRL
 */
function formatBRL(value: number | null | undefined): string {
  if (value == null) return '';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

/**
 * Extrai o primeiro nome de um nome completo
 */
function getFirstName(fullName: string | null | undefined): string {
  if (!fullName) return '';
  return fullName.split(' ')[0] || fullName;
}

/**
 * Extrai o último nome de um nome completo (tudo exceto o primeiro nome)
 */
function getLastName(fullName: string | null | undefined): string {
  if (!fullName) return '';
  const parts = fullName.split(' ');
  if (parts.length <= 1) return '';
  return parts.slice(1).join(' ');
}

/**
 * Processa um template de email substituindo placeholders por valores reais
 * 
 * @param template - Template com placeholders como {{firstName}}, {{company}}, etc
 * @param leadData - Dados do lead (name, email, phone, companies)
 * @param senderData - Dados do usuário que está enviando o email (full_name, email)
 * @returns Template processado com valores substituídos
 * 
 * Placeholders suportados:
 * - {{firstName}} - Primeiro nome do lead
 * - {{lastName}} - Último nome do lead
 * - {{fullName}} - Nome completo do lead
 * - {{company}} - Nome da empresa
 * - {{email}} - Email do lead
 * - {{phone}} - Telefone do lead
 * - {{senderName}} - Nome completo do usuário que está enviando
 * - {{senderFirstName}} - Primeiro nome do usuário que está enviando
 * - {{senderEmail}} - Email do usuário que está enviando
 */
export function processEmailTemplate(
  template: string | null | undefined,
  leadData?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    companies?: {
      name?: string | null;
    } | null;
  },
  senderData?: {
    full_name?: string | null;
    email?: string | null;
  },
  dealData?: {
    value?: number | null;
    stageName?: string | null;
  }
): string {
  if (!template) return '';

  // Preparar dados do template
  const templateData: TemplateData = {};

  // Dados do lead
  if (leadData) {
    const leadName = leadData.name || '';
    templateData.firstName = getFirstName(leadName);
    templateData.lastName = getLastName(leadName);
    templateData.fullName = leadName || '';
    templateData.email = leadData.email || '';
    templateData.phone = leadData.phone || '';
    templateData.company = leadData.companies?.name || '';
    // PT-BR aliases
    templateData.nome = leadName || '';
    templateData.primeiro_nome = getFirstName(leadName);
    templateData.empresa = leadData.companies?.name || '';
    templateData.telefone = leadData.phone || '';
  }

  // Dados do usuário que está enviando
  if (senderData) {
    const senderName = senderData.full_name || '';
    templateData.senderName = senderName;
    templateData.senderFirstName = getFirstName(senderName);
    templateData.senderEmail = senderData.email || '';
    // PT-BR aliases
    templateData.meu_nome = senderName;
    templateData.meu_email = senderData.email || '';
  }

  // Dados do deal
  if (dealData) {
    templateData.valor_deal = formatBRL(dealData.value);
    templateData.estagio_deal = dealData.stageName || '';
  }

  // Substituir placeholders no template
  let processedTemplate = template;

  // Lista de placeholders e seus valores
  const replacements: Array<[RegExp, string]> = [
    // EN (backwards compat)
    [/{{firstName}}/g, templateData.firstName || ''],
    [/{{lastName}}/g, templateData.lastName || ''],
    [/{{fullName}}/g, templateData.fullName || ''],
    [/{{company}}/g, templateData.company || ''],
    [/{{email}}/g, templateData.email || ''],
    [/{{phone}}/g, templateData.phone || ''],
    [/{{senderName}}/g, templateData.senderName || ''],
    [/{{senderFirstName}}/g, templateData.senderFirstName || ''],
    [/{{senderEmail}}/g, templateData.senderEmail || ''],
    // PT-BR
    [/{{nome}}/g, templateData.nome || ''],
    [/{{primeiro_nome}}/g, templateData.primeiro_nome || ''],
    [/{{empresa}}/g, templateData.empresa || ''],
    [/{{telefone}}/g, templateData.telefone || ''],
    [/{{valor_deal}}/g, templateData.valor_deal || ''],
    [/{{estagio_deal}}/g, templateData.estagio_deal || ''],
    [/{{meu_nome}}/g, templateData.meu_nome || ''],
    [/{{meu_email}}/g, templateData.meu_email || ''],
  ];

  // Aplicar todas as substituições
  replacements.forEach(([regex, value]) => {
    processedTemplate = processedTemplate.replace(regex, value);
  });

  return processedTemplate;
}

/**
 * Processa um template de email substituindo placeholders e marcando visualmente as partes substituídas
 * As partes substituídas são envolvidas em spans com classe CSS 'template-filled' para marcação visual
 * 
 * @param template - Template com placeholders como {{firstName}}, {{company}}, etc
 * @param leadData - Dados do lead (name, email, phone, companies)
 * @param senderData - Dados do usuário que está enviando o email (full_name, email)
 * @param markReplaced - Se true, marca visualmente as partes substituídas
 * @returns Template processado com valores substituídos e opcionalmente marcado
 */
export function processEmailTemplateWithMarking(
  template: string | null | undefined,
  leadData?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    companies?: {
      name?: string | null;
    } | null;
  },
  senderData?: {
    full_name?: string | null;
    email?: string | null;
  },
  dealData?: {
    value?: number | null;
    stageName?: string | null;
  },
  markReplaced: boolean = true
): string {
  if (!template) return '';

  // Preparar dados do template
  const templateData: TemplateData = {};

  // Dados do lead
  if (leadData) {
    const leadName = leadData.name || '';
    templateData.firstName = getFirstName(leadName);
    templateData.lastName = getLastName(leadName);
    templateData.fullName = leadName || '';
    templateData.email = leadData.email || '';
    templateData.phone = leadData.phone || '';
    templateData.company = leadData.companies?.name || '';
    // PT-BR aliases
    templateData.nome = leadName || '';
    templateData.primeiro_nome = getFirstName(leadName);
    templateData.empresa = leadData.companies?.name || '';
    templateData.telefone = leadData.phone || '';
  }

  // Dados do usuário que está enviando
  if (senderData) {
    const senderName = senderData.full_name || '';
    templateData.senderName = senderName;
    templateData.senderFirstName = getFirstName(senderName);
    templateData.senderEmail = senderData.email || '';
    // PT-BR aliases
    templateData.meu_nome = senderName;
    templateData.meu_email = senderData.email || '';
  }

  // Dados do deal
  if (dealData) {
    templateData.valor_deal = formatBRL(dealData.value);
    templateData.estagio_deal = dealData.stageName || '';
  }

  // Substituir placeholders no template e marcar visualmente
  let processedTemplate = template;

  // Lista de placeholders e seus valores
  const replacements: Array<[RegExp, string]> = [
    // EN (backwards compat)
    [/{{firstName}}/g, templateData.firstName || ''],
    [/{{lastName}}/g, templateData.lastName || ''],
    [/{{fullName}}/g, templateData.fullName || ''],
    [/{{company}}/g, templateData.company || ''],
    [/{{email}}/g, templateData.email || ''],
    [/{{phone}}/g, templateData.phone || ''],
    [/{{senderName}}/g, templateData.senderName || ''],
    [/{{senderFirstName}}/g, templateData.senderFirstName || ''],
    [/{{senderEmail}}/g, templateData.senderEmail || ''],
    // PT-BR
    [/{{nome}}/g, templateData.nome || ''],
    [/{{primeiro_nome}}/g, templateData.primeiro_nome || ''],
    [/{{empresa}}/g, templateData.empresa || ''],
    [/{{telefone}}/g, templateData.telefone || ''],
    [/{{valor_deal}}/g, templateData.valor_deal || ''],
    [/{{estagio_deal}}/g, templateData.estagio_deal || ''],
    [/{{meu_nome}}/g, templateData.meu_nome || ''],
    [/{{meu_email}}/g, templateData.meu_email || ''],
  ];

  // Se deve marcar visualmente, envolver valores substituídos em spans
  if (markReplaced) {
    replacements.forEach(([regex, value]) => {
      if (value) {
        // Substituir e marcar visualmente
        processedTemplate = processedTemplate.replace(
          regex,
          `<span class="template-filled" data-template-mark="true">${escapeHtml(value)}</span>`
        );
      } else {
        // Se não há valor, apenas remover o placeholder
        processedTemplate = processedTemplate.replace(regex, '');
      }
    });
  } else {
    // Apenas substituir sem marcação
    replacements.forEach(([regex, value]) => {
      processedTemplate = processedTemplate.replace(regex, value || '');
    });
  }

  return processedTemplate;
}

/**
 * Remove marcações visuais de template do HTML ou texto antes de enviar
 * @param content - HTML ou texto que pode conter spans com classe 'template-filled'
 * @returns Conteúdo limpo sem marcações visuais
 */
export function removeTemplateMarkings(content: string): string {
  if (!content) return '';
  
  // Se o conteúdo não contém HTML (não tem tags), usar regex simples
  if (!content.includes('<')) {
    return content;
  }
  
  // Para HTML, usar DOM para processar corretamente
  if (typeof document !== 'undefined') {
    // Criar um elemento temporário para processar o HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    // Encontrar todos os spans com marcação de template
    const markedSpans = tempDiv.querySelectorAll('span.template-filled[data-template-mark="true"]');
    
    // Substituir cada span pelo seu conteúdo interno
    markedSpans.forEach(span => {
      const parent = span.parentNode;
      if (parent) {
        const textNode = document.createTextNode(span.textContent || '');
        parent.replaceChild(textNode, span);
      }
    });
    
    return tempDiv.innerHTML;
  }
  
  // Fallback: usar regex para remover spans (menos preciso, mas funciona sem DOM)
  return content.replace(/<span[^>]*class="template-filled"[^>]*data-template-mark="true"[^>]*>(.*?)<\/span>/gi, '$1');
}

/**
 * Envolve variáveis não-resolvidas ({{variavel}}) em spans para highlight visual no editor
 * Deve ser chamada APÓS processEmailTemplateWithMarking para marcar o que sobrou
 */
export function highlightUnresolvedMergeFields(html: string): string {
  if (!html) return '';
  return html.replace(
    /\{\{(\w+)\}\}/g,
    '<span class="tiptap-merge-field">{{$1}}</span>'
  );
}

/** True se o HTML de email tem texto visível (não vazio / só tags / só &nbsp;). */
export function hasMeaningfulEmailHtml(html: string | null | undefined): boolean {
  if (html == null) return false;
  const trimmed = html.replace(/\u00a0/g, ' ').trim();
  if (!trimmed) return false;
  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.innerHTML = trimmed;
    return (div.textContent || '').replace(/\u00a0/g, ' ').trim().length > 0;
  }
  return trimmed.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().length > 0;
}

/**
 * Escapa caracteres HTML especiais para prevenir XSS
 */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, m => map[m]);
}

